/* =============================================================
   Build Your Own Clinical Kit — summary panel

   The configurator itself belongs to the Globo Product Options app.
   This script never drives selection; it only *reads* the app's state
   and mirrors it into the summary card (item list, count, subtotal,
   free-shipping bar).

   Reading rather than owning matters: the app handles conditional
   groups, engraving, quantity packs and the actual cart payload. If
   this file breaks, the builder still works — the shopper just loses
   the running summary, not the ability to buy.

   How a selection is read. Each option the app renders looks like:

     <div class="gpo-ov-item">
       <input type="radio|checkbox"
              data-field-name="Bag option"        ← category
              value="Black MedTech Backpack"      ← item name
              gpo-data-variant-id="42254536998969">
       <label>
         <span class="gpo-swatch__inner"><img class="swatch-image" src="…"></span>
         <div class="gpo-tooltip"><div class="gpo-tooltip__title">
           <span class="break-word">Black MedTech Backpack</span>
           <span class="addOn">(+ $39.95 USD)</span>   ← price
         </div></div>
       </label>
     </div>

   Prices come from `.addOn` because that is the only place the app
   exposes the *option's* price. It is display text, so it is parsed
   defensively and a failure to parse contributes 0 rather than NaN.
   ============================================================= */

(function () {
  'use strict';

  var MONEY_FALLBACK = '${{amount}}';

  /* Currency formatter matching Shopify's money_format placeholders.
     Only the two decimal-comma variants are handled because that is
     what the storefront can emit; anything else falls back to a plain
     two-decimal string rather than rendering a broken token. */
  function formatMoney(cents, format) {
    var value = (cents / 100).toFixed(2);
    var parts = value.split('.');
    var withCommas = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    if (!format) return '$' + withCommas + '.' + parts[1];

    return format
      .replace(/\{\{\s*amount\s*\}\}/g, withCommas + '.' + parts[1])
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, withCommas)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ',' + parts[1])
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.'));
  }

  /* "(+ $39.95 USD)" → 3995. Returns 0 when there is no parsable
     number, so an unpriced option never poisons the subtotal. */
  function parseAddOnCents(text) {
    if (!text) return 0;
    var match = String(text).replace(/,/g, '').match(/(\d+(?:\.\d{1,2})?)/);
    if (!match) return 0;
    var amount = parseFloat(match[1]);
    return isNaN(amount) ? 0 : Math.round(amount * 100);
  }

  /* Category icons. The app supplies only a heading string, so the icon
     is chosen from that text. Order matters — "field guide" must beat
     "guide", and "safety glasses" must beat "safety" — so this is an
     ordered list, not an object. */
  var CATEGORY_ICONS = [
    ['bag|backpack|carry|pack',        '<path d="M6 8h12v12H6zM9 8V6a3 3 0 1 1 6 0v2M9 12h6"/>'],
    ['stethoscope|steth',              '<path d="M6 3v5a6 6 0 0 0 12 0V3M4 3h4M16 3h4M12 14v2a4 4 0 0 0 8 0v-1"/><circle cx="20" cy="12" r="2"/>'],
    ['blood pressure|sphyg|cuff',      '<path d="M4 17a8 8 0 1 1 16 0M12 13l4-4M7 17h10"/>'],
    ['oximeter|pulse|spo',             '<path d="M4 12h3l2-5 3 10 2-5h6"/>'],
    ['shear|scissor',                  '<circle cx="6" cy="18" r="2.4"/><circle cx="18" cy="18" r="2.4"/><path d="M7.7 16.3 18 4M16.3 16.3 6 4"/>'],
    ['penlight|flashlight|light',      '<path d="m4 20 9-9M13 4l7 7-3 3-7-7z"/>'],
    ['glasses|goggle',                 '<circle cx="7" cy="14" r="3.2"/><circle cx="17" cy="14" r="3.2"/><path d="M10.2 14h3.6M3 11l2-3M21 11l-2-3"/>'],
    ['safety|vest|hazard',             '<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z"/>'],
    ['cpr|mask|rescue',                '<path d="M12 20s-7-4.3-7-9a4 4 0 0 1 7-2.6A4 4 0 0 1 19 11c0 4.7-7 9-7 9Z"/>'],
    ['notepad|clipboard|notes',        '<rect x="5" y="4" width="14" height="17" rx="2"/><path d="M9 4h6v3H9zM9 12h6M9 16h4"/>'],
    ['field guide|guide|book|manual',  '<path d="M5 4h9a3 3 0 0 1 3 3v13H8a3 3 0 0 1-3-3z"/><path d="M17 7h2v13H8"/>'],
    ['add-on|addon|extra|accessor|misc|miscellaneous', '<path d="m12 3 2.2 5.6L20 10l-4.4 3.6L17 20l-5-3-5 3 1.4-6.4L4 10l5.8-1.4z"/>'],
    ['essential|gear|kit',             '<rect x="3" y="7" width="18" height="13" rx="2"/><path d="M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2M12 11v5M9.5 13.5h5"/>'],
    ['instruction|how|info|start',     '<circle cx="12" cy="12" r="9"/><path d="M12 11v5M12 8h.01"/>']
  ];

  function iconFor(text) {
    var t = String(text || '').toLowerCase();
    for (var i = 0; i < CATEGORY_ICONS.length; i++) {
      if (new RegExp(CATEGORY_ICONS[i][0]).test(t)) return CATEGORY_ICONS[i][1];
    }
    return '<rect x="4" y="7" width="16" height="13" rx="2"/><path d="M4 11h16"/>';
  }

  function svgWrap(inner) {
    return '<svg viewBox="0 0 24 24" aria-hidden="true">' + inner + '</svg>';
  }

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function BYOSummary(root) {
    this.root = root;
    this.threshold = parseInt(root.getAttribute('data-free-shipping-threshold'), 10) || 0;
    this.moneyFormat = root.getAttribute('data-money-format') || MONEY_FALLBACK;

    this.itemsEl = root.querySelector('[data-byo-items]');
    this.emptyEl = root.querySelector('[data-byo-empty]');
    this.countEl = root.querySelector('[data-byo-count]');
    this.subtotalEl = root.querySelector('[data-byo-subtotal]');
    this.progressEl = root.querySelector('[data-byo-progress]');
    this.progressFill = root.querySelector('[data-byo-progress-fill]');
    this.progressText = root.querySelector('[data-byo-progress-text]');
    this.resetBtn = root.querySelector('[data-byo-reset]');
    this.helperEl = root.querySelector('.byo-atc-helper');
    this.loadingEl = root.querySelector('[data-byo-loading]');
    this.optionsCol = root.querySelector('[data-byo-options]');

    this.bind();
    this.watchForApp();
  }

  BYOSummary.prototype.bind = function () {
    var self = this;

    /* Delegated: the app rewrites its own DOM as conditional groups
       appear and disappear, so per-input listeners would go stale. */
    this.root.addEventListener('change', function (event) {
      if (event.target.closest('.gpo-container')) self.update();
    });

    /* Sparks fire on click rather than change so the burst is anchored
       to where the shopper actually pressed, and so re-clicking an
       already-selected option (no change event) still feels alive. */
    this.root.addEventListener('click', function (event) {
      var label = event.target.closest('.gpo-ov-item > label');
      if (label) self.spark(label);
    });

    if (this.resetBtn) {
      this.resetBtn.addEventListener('click', function () {
        self.reset();
      });
    }

    /* A saved kit opened from the wishlist while already on this page only
       changes the hash, so there is no reload to pick it up. Clear the
       current parts first so the saved kit is not merged into them. */
    window.addEventListener('hashchange', function () {
      if ((location.hash || '').indexOf(KIT_HASH) === -1) return;
      if (!self.root.querySelector('.gpo-container')) return;
      self.reset();
      setTimeout(function () {
        self.restoreKit();
      }, 250);
    });
  };

  /* The app injects asynchronously. Watch for `.gpo-container`, then
     stop observing — after that, `change` events are enough. */
  BYOSummary.prototype.watchForApp = function () {
    var self = this;
    var container = this.root.querySelector('.gpo-container');

    if (container) {
      this.onAppReady();
      return;
    }

    var observer = new MutationObserver(function () {
      if (self.root.querySelector('.gpo-container')) {
        observer.disconnect();
        self.onAppReady();
      }
    });

    observer.observe(this.root, { childList: true, subtree: true });

    /* Give up watching after 15s so a missing app doesn't leave an
       observer running for the life of the page. */
    setTimeout(function () {
      observer.disconnect();
      if (self.loadingEl) self.loadingEl.hidden = true;
    }, 15000);
  };

  BYOSummary.prototype.onAppReady = function () {
    if (this.loadingEl) this.loadingEl.hidden = true;
    this.moveOptionsIntoColumn();
    this.decorate();
    this.update();
    this.watchAppMutations();
    this.restoreKit();
  };

  /* All the cosmetic passes over the app's markup, in one place.

     `decorating` guards re-entrancy: these passes mutate `.gpo-container`
     (adding icons, rewriting price text), which the MutationObserver
     below is watching — without the guard each pass would retrigger
     itself forever. */
  BYOSummary.prototype.decorate = function () {
    if (this.decorating) return;
    this.decorating = true;

    this.addCategoryIcons();
    this.markDenseGroups();
    this.tidyPrices();
    this.markParentOptions();
    this.syncGroupBadges();

    this.decorating = false;
  };

  /* Globo renders asynchronously and re-renders whole groups when a
     conditional option changes, which wipes our decorations and restores
     its own raw "(+ $39.95 USD)" price text. Watching the container and
     re-decorating is what keeps the styling stable — a one-shot pass on
     load only ever caught whichever groups happened to exist by then. */
  BYOSummary.prototype.watchAppMutations = function () {
    var self = this;
    var container = this.root.querySelector('.gpo-container');
    if (!container || this.appObserver) return;

    var pending = null;
    this.appObserver = new MutationObserver(function () {
      if (pending) clearTimeout(pending);
      pending = setTimeout(function () {
        self.decorate();
        self.update();
      }, 120);
    });

    this.appObserver.observe(container, { childList: true, subtree: true });
  };

  /* One icon per category heading, chosen from the heading text. Runs
     once — the flag stops conditional groups re-appearing with a second
     icon stacked on the first. */
  BYOSummary.prototype.addCategoryIcons = function () {
    var headings = this.root.querySelectorAll('.gpo-heading');

    Array.prototype.forEach.call(headings, function (heading) {
      if (heading.dataset.byoIcon === 'true') return;
      heading.dataset.byoIcon = 'true';

      var title = heading.textContent.replace(/\s+/g, ' ').trim();
      if (!title) return;

      var span = document.createElement('span');
      span.className = 'byo-cat-ico';
      span.setAttribute('aria-hidden', 'true');
      span.innerHTML = svgWrap(iconFor(title));
      heading.insertBefore(span, heading.firstChild);
    });
  };

  /* "Added" pill in the header of any category holding a selection.
     Removed again when the last option in that category is cleared, so
     the badge always reflects the current kit rather than accumulating. */
  BYOSummary.prototype.syncGroupBadges = function () {
    var groups = this.root.querySelectorAll('.gpo-container .gpo-group');

    Array.prototype.forEach.call(groups, function (group) {
      var heading = group.querySelector('.gpo-heading');
      if (!heading) return;

      var checked = Array.prototype.some.call(
        group.querySelectorAll('input:checked'),
        function (input) {
          var item = input.closest('.gpo-ov-item') || input.parentElement;
          return item && item.offsetParent !== null;
        }
      );

      var badge = heading.querySelector('.byo-group-badge');

      if (checked && !badge) {
        badge = document.createElement('span');
        badge.className = 'byo-group-badge';
        badge.innerHTML =
          '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m5 13 4 4L19 7"/></svg><span>Added</span>';
        heading.appendChild(badge);
      } else if (!checked && badge) {
        badge.remove();
      }
    });
  };

  /* Eight particles thrown outward from the tile's tick, matching the
     prototype's burst. Everything is position:fixed off the tick's
     viewport rect, so it works regardless of scroll container. */
  BYOSummary.prototype.spark = function (label) {
    if (prefersReducedMotion()) return;

    var rect = label.getBoundingClientRect();
    var originX = rect.right - 19;
    var originY = rect.top + 19;
    var COUNT = 8;

    for (var i = 0; i < COUNT; i++) {
      var angle = (Math.PI * 2 * i) / COUNT + Math.random() * 0.4;
      var distance = 26 + Math.random() * 22;
      var dot = document.createElement('span');

      dot.className = 'byo-spark';
      dot.style.left = originX + 'px';
      dot.style.top = originY + 'px';
      dot.style.setProperty('--dx', Math.cos(angle) * distance + 'px');
      dot.style.setProperty('--dy', Math.sin(angle) * distance + 'px');
      dot.style.setProperty('--dur', 0.55 + Math.random() * 0.35 + 's');

      document.body.appendChild(dot);

      /* animationend can not fire if the element is removed from the
         layout early, so a timeout is the reliable cleanup. */
      (function (node) {
        setTimeout(function () {
          node.remove();
        }, 1100);
      })(dot);
    }
  };

  /* Globo appends its container to the product form. The form wraps the
     whole two-column grid (it has to — the app only intercepts an
     add-to-cart button that lives inside the form), so the container
     lands after the grid rather than inside the left column.

     Moving it is safe: the destination is inside the same <form>, so
     every option input is still submitted. This repositions, it never
     reparents out of the form — doing that would drop the options from
     the cart payload. */
  BYOSummary.prototype.moveOptionsIntoColumn = function () {
    if (!this.optionsCol) return;

    var container = this.root.querySelector('.gpo-container');
    if (!container || this.optionsCol.contains(container)) return;

    var form = container.closest('form');
    this.optionsCol.appendChild(container);

    /* The hidden inputs carry the properties Shopify receives, so they
       must travel with the container and stay within the same form. */
    var hidden = this.root.querySelector('.gpo-hidden-inputs');
    if (hidden && !this.optionsCol.contains(hidden)) this.optionsCol.appendChild(hidden);

    if (form && !form.contains(container)) {
      /* Should be impossible given the markup, but if the layout ever
         changes so the column sits outside the form, put the container
         back rather than silently posting a kit with no options. */
      form.appendChild(container);
      if (hidden) form.appendChild(hidden);
    }
  };

  /* Colour and size groups can carry 30–64 values. Tagging them lets
     the stylesheet swap those to a compact swatch grid instead of the
     two-up product tiles used for real component choices. */
  /* The app prints prices as "(+ $39.95 USD)". The design shows plain
     "$39.95", and the brackets/currency make long option names wrap.
     Rewritten for display only — the amount is untouched, and the app
     charges from its own data, never from this text. Idempotent, and
     re-run after every update because the app rewrites these nodes when
     conditional groups change. */
  BYOSummary.prototype.tidyPrices = function () {
    var nodes = this.root.querySelectorAll('.gpo-container .addOn');

    Array.prototype.forEach.call(nodes, function (node) {
      var raw = node.getAttribute('data-byo-raw') || node.textContent;
      if (!node.getAttribute('data-byo-raw')) node.setAttribute('data-byo-raw', raw);

      var match = String(raw).match(/[^\d\s]*\s*\d[\d,]*(?:\.\d{1,2})?/);
      if (!match) return;

      var tidy = match[0].replace(/^[+\s(]*/, '').trim();
      if (tidy && node.textContent !== tidy) node.textContent = tidy;
    });
  };

  /* Flag tiles whose price lives in a follow-up group so they don't just
     show blank. Restricted to real product tiles (they have a swatch
     image) — plain Yes/No buttons are configuration, not purchases, and
     correctly show nothing. */
  BYOSummary.prototype.markParentOptions = function () {
    var items = this.root.querySelectorAll('.gpo-container .gpo-ov-item');

    Array.prototype.forEach.call(items, function (item) {
      if (item.querySelector('.addOn') || !item.querySelector('img.swatch-image')) {
        var stale = item.querySelector('.byo-opt-hint');
        if (stale) stale.remove();
        return;
      }
      if (item.querySelector('.byo-opt-hint')) return;

      var host = item.querySelector('.gpo-tooltip__title') || item.querySelector('label');
      if (!host) return;

      var hint = document.createElement('span');
      hint.className = 'byo-opt-hint';
      hint.textContent = 'Select options';
      host.appendChild(hint);
    });
  };

  BYOSummary.prototype.markDenseGroups = function () {
    var DENSE_AT = 8;
    var wrappers = this.root.querySelectorAll('.gpo-ov-wrapper');

    Array.prototype.forEach.call(wrappers, function (wrapper) {
      var count = wrapper.querySelectorAll('.gpo-ov-item').length;
      wrapper.classList.toggle('gpo-ov-wrapper--dense', count >= DENSE_AT);
    });
  };

  /* Only inputs the shopper can actually see count. The app keeps
     unselected conditional groups in the DOM, and their inputs can
     still be checked from a previous choice — including those would
     bill for a stethoscope colour after switching to a different
     stethoscope. offsetParent is null for anything display:none. */
  BYOSummary.prototype.getSelections = function () {
    var inputs = this.root.querySelectorAll(
      '.gpo-container .gpo-ov-item > input:checked, .gpo-container .gpo-choicelist input:checked, .gpo-container .gpo-radio input:checked'
    );

    return Array.prototype.filter
      .call(inputs, function (input) {
        var item = input.closest('.gpo-ov-item') || input.parentElement;
        return item && item.offsetParent !== null;
      })
      .map(function (input) {
        var item = input.closest('.gpo-ov-item') || input.parentElement;
        var addOn = item.querySelector('.addOn');
        var img = item.querySelector('img.swatch-image');
        var nameEl = item.querySelector('.gpo-tooltip__title .break-word');

        return {
          group: input.getAttribute('data-field-name') || '',
          name: (nameEl && nameEl.textContent.trim()) || input.value || '',
          cents: parseAddOnCents(addOn && addOn.textContent),
          image: img ? img.getAttribute('src') : null
        };
      })
      .filter(function (sel) {
        if (!sel.name) return false;

        /* Not every checked input is a kit component. The option set
           includes plain yes/no questions ("Star of Life on Backpack?")
           which are configuration, not line items — counting those gave
           a summary reading "1 item · $0.00" before the shopper had
           chosen anything. A real component always has either a price
           or a product swatch image, so that is the test. */
        return sel.cents > 0 || !!sel.image;
      });
  };

  BYOSummary.prototype.update = function () {
    var selections = this.getSelections();
    var subtotal = selections.reduce(function (sum, sel) {
      return sum + sel.cents;
    }, 0);

    this.renderItems(selections);
    this.renderCount(selections.length);
    this.renderSubtotal(subtotal);
    this.renderProgress(subtotal);
    this.syncSaveButton(selections, subtotal);

    /* The helper only explains why the button is disabled, so it goes
       away the moment it stops being true. */
    var atc = this.getAtcButton();
    if (atc) atc.disabled = selections.length === 0;
    if (this.helperEl) this.helperEl.hidden = selections.length > 0;
  };

  /* Always re-query. Globo *clones* the add-to-cart button in order to
     intercept the click (the clone is tagged `gpo-clone-atc-button`),
     which replaces the node. A reference cached at construction time
     points at the discarded original, so disabling it has no effect on
     what the shopper actually sees — the visible button stayed disabled
     forever and the kit could never be added. */
  BYOSummary.prototype.getAtcButton = function () {
    return this.root.querySelector('.byo-atc');
  };

  BYOSummary.prototype.renderItems = function (selections) {
    if (!this.itemsEl) return;

    Array.prototype.forEach.call(this.itemsEl.querySelectorAll('.byo-item'), function (el) {
      el.remove();
    });

    if (this.emptyEl) this.emptyEl.hidden = selections.length > 0;

    var frag = document.createDocumentFragment();
    var format = this.moneyFormat;

    selections.forEach(function (sel) {
      var row = document.createElement('div');
      row.className = 'byo-item';

      if (sel.image) {
        var img = document.createElement('img');
        img.className = 'byo-item__thumb';
        img.src = sel.image;
        img.alt = '';
        img.loading = 'lazy';
        row.appendChild(img);
      } else {
        var blank = document.createElement('span');
        blank.className = 'byo-item__thumb byo-item__thumb--blank';
        row.appendChild(blank);
      }

      var name = document.createElement('span');
      name.className = 'byo-item__name';
      if (sel.group) {
        var group = document.createElement('span');
        group.className = 'byo-item__group';
        group.textContent = sel.group;
        name.appendChild(group);
      }
      name.appendChild(document.createTextNode(sel.name));
      row.appendChild(name);

      var price = document.createElement('span');
      price.className = 'byo-item__price';
      price.textContent = sel.cents > 0 ? formatMoney(sel.cents, format) : '—';
      row.appendChild(price);

      frag.appendChild(row);
    });

    this.itemsEl.appendChild(frag);
  };

  BYOSummary.prototype.renderCount = function (count) {
    if (!this.countEl) return;
    this.countEl.textContent = count + (count === 1 ? ' item' : ' items');
  };

  BYOSummary.prototype.renderSubtotal = function (cents) {
    if (!this.subtotalEl) return;
    this.subtotalEl.textContent = formatMoney(cents, this.moneyFormat);
  };

  BYOSummary.prototype.renderProgress = function (cents) {
    if (!this.progressEl || !this.threshold) return;

    this.progressEl.hidden = cents <= 0;

    var pct = Math.min(100, (cents / this.threshold) * 100);
    if (this.progressFill) this.progressFill.style.width = pct + '%';

    var remaining = this.threshold - cents;
    var done = remaining <= 0;

    this.progressEl.classList.toggle('is-complete', done);

    if (this.progressText) {
      this.progressText.textContent = '';

      if (done) {
        this.progressText.textContent = 'Your kit ships free';
      } else {
        /* Built from nodes rather than innerHTML so the amount is bold
           (as in the design) without ever parsing markup. */
        var amount = document.createElement('strong');
        amount.textContent = formatMoney(remaining, this.moneyFormat);
        this.progressText.appendChild(document.createTextNode('Add '));
        this.progressText.appendChild(amount);
        this.progressText.appendChild(document.createTextNode(' more for free shipping'));
      }
    }
  };

  /* Clears the app's inputs and fires the events it listens for, so
     its own conditional logic re-runs and hides dependent groups. */
  BYOSummary.prototype.reset = function () {
    var inputs = this.root.querySelectorAll('.gpo-container input:checked');

    Array.prototype.forEach.call(inputs, function (input) {
      input.checked = false;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });

    var texts = this.root.querySelectorAll('.gpo-container input[type="text"], .gpo-container textarea');
    Array.prototype.forEach.call(texts, function (input) {
      input.value = '';
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });

    var selects = this.root.querySelectorAll('.gpo-container select');
    Array.prototype.forEach.call(selects, function (select) {
      select.selectedIndex = 0;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    });

    this.update();
  };

  /* ── Saving a built kit to the wishlist ───────────────────
     The hero's heart (sections/byo-hero.liquid, [data-byo-save]) is an
     ordinary wishlist button: assets/wishlist.js reads its data-favorite-*
     attributes on click. Here those attributes are kept describing the kit
     on screen, so a click saves *this* kit rather than the bare product:

       id    byo-<product id> — ONE saved kit per builder. Saving and then
             adding more parts must not leave a second kit in the list, so
             the id does not depend on the parts; instead, while the kit is
             saved, the saved entry is rewritten in place on every change
             (MTWishlist.update), and the heart stays filled throughout
       url   the product page plus #kit=<parts>, which restoreKit() reads
             back to re-select those parts

     With nothing picked the button is disabled and the saved entry is left
     as it was — an emptied builder is not a kit worth overwriting it with. */
  var KIT_HASH = 'kit=';

  /* The first build of this feature gave every combination of parts its own
     id (byo-<product>-<hash>), so a shopper who saved, added a part and
     saved again ended up with two kits. Those entries are folded into the
     single one: the most recently saved survives under the new id, the rest
     go. Runs once per page load and only writes when there is something to
     fold. */
  function mergeLegacyKits(productId) {
    var store = window.MTWishlist;
    if (!store || !store.write) return;

    var id = 'byo-' + productId;
    var prefix = id + '-';
    var items = store.read();
    var kept = false;
    var changed = false;

    var merged = items.filter(function (entry) {
      var entryId = String(entry.id);
      if (entryId === id) {
        if (kept) {
          changed = true;
          return false;
        }
        kept = true;
        return true;
      }
      if (entryId.indexOf(prefix) !== 0) return true;
      changed = true;
      /* The list is newest first, so the first legacy kit is the latest. */
      if (kept || entryId === prefix + 'empty') return false;
      entry.id = id;
      kept = true;
      return true;
    });

    if (changed) store.write(merged);
  }

  BYOSummary.prototype.syncSaveButton = function (selections, subtotal) {
    var button = document.querySelector('[data-byo-save]');
    if (!button) return;
    var data = button.dataset;

    /* The Liquid values, kept once so an emptied kit can go back to them. */
    if (!this.saveBase) {
      this.saveBase = {
        id: data.byoProductId || data.favoriteProduct,
        title: data.favoriteTitle || '',
        url: data.favoriteUrl || location.pathname,
        image: data.favoriteImage || '',
        price: data.favoritePrice || '',
        label: button.getAttribute('aria-label') || ''
      };
      mergeLegacyKits(this.saveBase.id);
    }
    var base = this.saveBase;
    var kitId = 'byo-' + base.id;

    if (!selections.length) {
      /* Nothing to save until a part is picked: the button stays disabled
         (it ships disabled from Liquid too), and its id matches no saved
         entry, so an earlier save of the bare product does not show the
         heart as filled over an empty builder. */
      button.disabled = true;
      button.setAttribute('title', base.label);
      data.favoriteProduct = 'byo-' + base.id + '-empty';
      data.favoriteTitle = base.title;
      data.favoriteUrl = base.url;
      data.favoriteImage = base.image;
      data.favoritePrice = base.price;
      delete data.favoriteNote;
      delete data.favoriteAction;
      button.setAttribute('aria-label', base.label);
    } else {
      button.disabled = false;
      button.removeAttribute('title');
      var pairs = selections.map(function (sel) {
        return [sel.group, sel.name];
      });
      var names = selections.map(function (sel) {
        return sel.name;
      });
      var withImage = selections.filter(function (sel) {
        return !!sel.image;
      })[0];

      data.favoriteProduct = kitId;
      data.favoriteTitle = 'My ' + base.title.replace(/^Build Your Own\s+/i, '');
      data.favoriteUrl = base.url.split('#')[0] + '#' + KIT_HASH + encodeURIComponent(JSON.stringify(pairs));
      data.favoriteImage = (withImage && withImage.image) || base.image;
      data.favoritePrice = formatMoney(subtotal, this.moneyFormat);
      data.favoriteNote =
        selections.length + (selections.length === 1 ? ' item' : ' items') + ' · ' + names.join(', ');
      data.favoriteAction = 'Continue building';
      button.setAttribute('aria-label', 'Save this kit (' + selections.length + ' items)');

      /* Already saved: keep the saved kit in step with what is on screen.
         Held off while a saved kit is being re-selected (restoreKit), whose
         half-applied passes would otherwise be written over the full kit. */
      if (window.MTWishlist && !this.restoring) {
        window.MTWishlist.update(window.MTWishlist.itemFromButton(button));
      }
    }

    if (window.MTWishlist) window.MTWishlist.syncButtons(button.parentNode);
  };

  /* Re-selects a kit opened from the wishlist (#kit=…). Picking a part can
     reveal the groups that depend on it (a stethoscope's colours, say), and
     the app renders those a moment later, so this works in passes: each
     pass clicks whatever saved part is now visible, then waits for the app.
     Clicking rather than setting `checked` lets the app run its own logic. */
  BYOSummary.prototype.restoreKit = function () {
    var hash = location.hash || '';
    var at = hash.indexOf(KIT_HASH);
    if (at === -1) return;

    var pending;
    try {
      pending = JSON.parse(decodeURIComponent(hash.slice(at + KIT_HASH.length)));
    } catch (error) {
      return;
    }
    if (!Array.isArray(pending) || !pending.length) return;

    var self = this;
    var container = this.root.querySelector('.gpo-container');
    if (!container) return;
    this.restoring = true;

    function nameOf(input) {
      var item = input.closest('.gpo-ov-item') || input.parentElement;
      var nameEl = item && item.querySelector('.gpo-tooltip__title .break-word');
      return (nameEl && nameEl.textContent.trim()) || input.value || '';
    }

    function findInput(group, name) {
      var inputs = container.querySelectorAll('input[type="radio"], input[type="checkbox"]');
      for (var i = 0; i < inputs.length; i++) {
        var input = inputs[i];
        if ((input.getAttribute('data-field-name') || '') !== group) continue;
        if (nameOf(input) !== name) continue;
        var item = input.closest('.gpo-ov-item') || input.parentElement;
        if (item && item.offsetParent !== null) return input;
      }
      return null;
    }

    var passes = 0;
    function pass() {
      passes++;
      pending = pending.filter(function (pair) {
        var input = findInput(pair[0], pair[1]);
        if (!input) return true;
        if (!input.checked) input.click();
        return false;
      });

      if (pending.length && passes < 12) {
        setTimeout(pass, 250);
        return;
      }

      /* Done (or as far as the current option set allows — a part that has
         since been removed from the builder simply is not re-selected).
         The hash goes so a reload does not re-apply over later changes. */
      if (window.history && history.replaceState) {
        history.replaceState(null, '', location.pathname + location.search);
      }
      self.restoring = false;
      self.update();
    }

    pass();
  };

  function init() {
    var roots = document.querySelectorAll('[data-byo-root]');
    Array.prototype.forEach.call(roots, function (root) {
      if (root.dataset.byoReady === 'true') return;
      root.dataset.byoReady = 'true';
      new BYOSummary(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Theme editor: re-init when this section is re-rendered. */
  document.addEventListener('shopify:section:load', init);
})();
